<template>
  <div style="width: 100%;" v-loading.body="loading" element-loading-text="Loading...">
      <el-row type="flex" align="middle" justify="end" class="headerRow">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.5em;">
            BANK RECONCILIATION REPORTING
          </el-row>
        </el-col>
        <el-col class="small">
            <a @click="showJson = ! showJson">.&nbsp;</a>
        </el-col>
        <el-col>
          <el-row type="flex" align="middle" justify="end">
            <el-button
              type="primary"
              @click="$router.push(
                {
                  name: 'ViewBankRec',
                  params: { id: $route.query.bankrec_id }
                })">
              Back
            </el-button>
            <el-button plain @click="generateReport" :disabled="disableReportRun">
              Run report
            </el-button>
          </el-row>
        </el-col>
      </el-row>
      <el-row type="flex" align="middle" justify="end" class="headerRow">
        <el-col>
          <el-row type="flex" align="middle" justify="start" style="font-size: 1.2em;">
            Report Options
          </el-row>
        </el-col>
      </el-row>

      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="4" class="right">
          Summary/Detail
        </el-col>
        <el-col :span="4">
          <el-radio-group v-model="reportOptions.type" @change="updateReportType">
            <el-radio-button value="detail">Detail</el-radio-button>
            <el-radio-button value="summary">Summary</el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>

      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="4" class="right">
          Bank Account<sup>*</sup>
        </el-col>
        <el-col :span="8">
          <el-select
            v-model="selectedCodes.bank_id"
            :multiple="false" placeholder="Bank Account" @change="updateBank">
            <el-option
              v-for="opt in allCodes.banks"
              :key="opt.id"
              :label="opt.code + ' - ' + opt.account_number"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
      </el-row>

      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="4" class="right">
          Bank Statement<sup>*</sup>
        </el-col>
        <el-col :span="8">
          <el-select
            v-model="selectedReconId"
            :multiple="false" placeholder="Statement Date" value-key="id" @change="updateBankRec">
            <el-option
              v-for="opt in reversedBankrecs"
              :key="opt.id"
              :label="opt.end"
              :value="opt.id">
            </el-option>
          </el-select>
        </el-col>
      </el-row>

      <el-row type="flex" align="middle" justify="start" class="headerRow">
        <el-col :span="4" class="right">
        <el-checkbox v-model="reportOptions.excludeOutstanding"></el-checkbox>
        </el-col>
        <el-col :span="8">
          Exclude Transactions Past Statement Date
        </el-col>
      </el-row>

      <el-row type="flex" align="middle" justify="end" class="headerRow">
        <el-col>
          <p><sup>*</sup> Required</p>
        </el-col>
      </el-row>
      <el-row>
        <el-col v-if="showJson">
          <textarea id="output" style="width: 100%; height: 500px;" v-model="prettyJson"></textarea>
          <el-button plain @click="generateCustomReport" :disabled="disableReportRun">
            Run Customized Report
          </el-button>
        </el-col>
      </el-row>
  </div>
</template>

<script>
  /* eslint-disable no-unused-vars */
  import { mapGetters } from 'vuex'
  import { notify } from '../../utils/helpers/Notify'
  import { buildInitPromiseIterable } from '@/utils/helpers/BuildPromiseIterable'
  import { sortObjectByProperty } from '@/utils/helpers/ObjectSorting'
  import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'
  import { getSimpleDate } from '@/utils/helpers/FormatDate'
  import { reportMixins } from '@/Mixins/Reports'

  export default {
    name: 'BankRecReport',
    mixins: [ reportMixins ],
    props: {
    },
    data () {
      const { getJasperHost } = this.$store.getters
      return {
        selectedReconId: undefined,
        reportOptions: {
          transactionStatuses: [ 'posted', 'closed' ],
          accountQuickPick: 'all',
          type: 'detail',
          sort: 'account',
          status: 'all',
          format: 'pdf',
          excludeOutstanding: true,
          report_currency_id: undefined
        },

        allCodes: {}, // Show codes, e.g. accounts, series, locations, sets, etc.
        selectedCodes: {
          period_start: undefined,
          period_end: undefined,
          account_id: [],
          series_id: [],
          location_id: [],
          set_id: [],
          free_field_1_id: [],
          company_id: undefined,
          production_id: undefined,
          source_code_id: [],
          bank_id: undefined
        },
        jasperSettings: {
          reportServer: `${getJasperHost}rest_v2/reports/Revolution/Phoenix/`,
          reportName: 'bank_rec_detail',
          reportTitle: 'Bank Reconciliation',
          report_subtitle: 'Sorted by Check'
        },

        vendors: undefined,

        transactionTypes: {     // Types of transactions and whether we need them or not
          'aps': { name: 'AP', include: true },
          'jes': { name: 'JE', include: true },
          'pcs': { name: 'PC', include: true },
          'cks': { name: 'CK', include: true },
          'efts': { name: 'EFT', include: true },
          'wts': { name: 'WIRE', include: true },
          'mcs': { name: 'MC', include: true }
        },
        unserializedData: {      // Raw data from API
          aps: {
            headers: {},
            lines: {}
          },
          jes: {
            headers: {},
            lines: {}
          },
          pcs: {
            headers: {},
            lines: {}
          },
          cks: {
            headers: {},
            lines: {}
          },
          pos: {
            headers: {},
            lines: {}
          }
        },
        sortedAccounts: [],     // Sorted data by accounts, transaction ID.  Needed for 90% of reports
        reportJson: undefined,
        prettyJson: undefined,
        showJson: false,
        reportNotif: undefined,
        collapseExpenses: false,
        preLoadedStatement: {},
        loading: false
      }
    },

    computed: {
      username () { return this.$store.getters.getUser.username },
      // Enable "Run report" as soon as a bank + statement are chosen — whether the user picked them or
      // they were preselected from the route query. Computing this (instead of imperatively flipping a
      // flag only inside the @change handlers) means a programmatic selection enables the button too;
      // the previous data flag stayed `true` on preload because @change never fires for programmatic
      // v-model changes. `loading` keeps it disabled while a report is being generated.
      disableReportRun () {
        return this.loading || !this.selectedCodes.bank_id || !this.selectedReconId
      },
      includedTypes () { return Object.values(this.transactionTypes).filter(types => types.include === true).map(type => type.name) },
      accounts () {
        let accountsNew = []
        for (let i in this.allCodes.accounts) {
          if (this.allCodes.accounts[i].type.toLowerCase() === 'header') continue
          accountsNew[i] = this.allCodes.accounts[i]
          accountsNew[i]['display_name'] = this.allCodes.accounts[i]['code'] + ' - ' + this.allCodes.accounts[i]['name']
        }
        return accountsNew
      },
      // Reversed COPY of the bankrec statements. Reversing inline in the v-for (`(allCodes.bankrecs ||
      // []).reverse()`) mutated the reactive array on every render, which re-triggered the render and
      // threw "Maximum recursive updates exceeded in <ElSelect>" — leaving the select (and thus the
      // Run button) stuck. Slicing first keeps the source untouched.
      reversedBankrecs () {
        return [...(this.allCodes.bankrecs || [])].reverse()
      },
      selectedBankRec () {
        if (this.selectedReconId) {
          return this.allCodes.bankrecs.find(({id}) => id === parseInt(this.selectedReconId))
        } else {
          return false
        }
      },
      statement_date () {
        if (this.selectedReconId) {
          return this.selectedBankRec.end
        } else {
          return false
        }
      },
      ...mapGetters({
        getAccountBalance: 'getAccountBalanceById',
        getBanks: 'getBanks',
        getBankStatements: 'getBankStatementsByBank',
        getBankRecs: 'getBankRecs',
        getCurrencies: 'getCurrencies',
        meta: 'getBankRecMetadata',
        receiving: 'getBankrecFetchStatus'
      })
    },

    watch: {
    },

    methods: {
      resetComponent () {
        this.reportJson = this.vendors = this.sortedAccounts = undefined
        this.clearStore()
      },
      clearStore () {
        // TODO
      },
      updateReportType (type) {
        this.jasperSettings.reportName = 'bank_rec_' + type
      },
      updateBank () {
        let bank = this.allCodes.banks.find(({id}) => id === this.selectedCodes.bank_id)
        this.reportOptions.report_currency_id = bank.currency
        this.$store.dispatch('fetchAccountBalanceById', { id: bank.default_account_cash, currency: bank.currency })
        this.$store.dispatch('fetchBankStatementsByBank', bank.id)
          .then(response => {
            // Hack to get Element to update the options field
            let reportTypeCurrent = this.reportOptions.type
            this.reportOptions.type = ''
            this.reportOptions.type = reportTypeCurrent
            this.allCodes.bankrecs = response.statements
            // Hack to add current draft since API only returns completed statements
            if (this.preLoadedStatement.bank === bank.id && !(this.allCodes.bankrecs || []).find(({id}) => parseInt(id) === parseInt(this.preLoadedStatement.id))) {
              this.allCodes.bankrecs.push(this.preLoadedStatement)
            }
          })
          .catch(e => console.log(e))
      },
      updateBankRec () {
        // Run-button enabled state is derived from selectedReconId via the disableReportRun computed.
      },
      determinePreviousBalance () {
        // const bankrecs = this.allCodes.bankrecs.filter(({bank}) => bank === this.selectedCodes.bank_id)
        const bankrecs = this.allCodes.bankrecs.map(rec => rec.id)
        if (bankrecs.length <= 1) return 0

        const previousIndex = Math.max.apply(Math, bankrecs.filter(index => index < this.selectedReconId))
        const previousBankRec = this.allCodes.bankrecs.find(({id}) => id === previousIndex)

        return (previousBankRec && previousBankRec.hasOwnProperty('balance_decimal')) ? previousBankRec.balance_decimal : 0
      },
      buildReconReport (sortedAccounts) {
        return new Promise((resolve, reject) => {
          const selectedBank = this.allCodes.banks.find(({id}) => id === this.selectedCodes.bank_id)
          const previousBalance = this.determinePreviousBalance()

          let json = {
            'report': {
              report_name: this.jasperSettings.report_name,
              report_title: this.jasperSettings.reportTitle + ' - ' + selectedBank.name,
              report_subtitle: this.selectedBankRec.completed_time ? '' : 'DRAFT',
              printed_by: this.username,
              date_run: this.today(),
              company: (this.selectedCodes.company_id) ? (this.allCodes.companies.find(({id}) => id === this.selectedCodes.company_id).name) : this.allCodes.companies[0].name,
              project: (this.selectedCodes.production_id) ? (this.allCodes.productions.find(({id}) => id === this.selectedCodes.production_id).name) : this.allCodes.productions[0].name,
              period: (this.selectedCodes.period_start || 'Beginning') + ' - ' + (this.selectedCodes.period_end || 'End'),
              currency: (this.reportOptions.report_currency_id) ? (this.allCodes.currencies.find(({id}) => id === this.reportOptions.report_currency_id).code) : this.currencies[0].code,
              date_start: (this.selectedCodes.period_start) ? (this.allCodes.periods.find(({id}) => id === this.selectedCodes.period_start).start) : '0000/00/00',
              date_end: (this.selectedCodes.period_end) ? (this.allCodes.periods.find(({id}) => id === this.selectedCodes.period_end).end) : this.today(),
              transactions: 'All',
              episodes: (this.selectedCodes.series_id && this.selectedCodes.series_id.length > 0) ? (this.allCodes.series.find(({id}) => this.selectedCodes.series_id.includes(id)).code) : 'All',
              locations: (this.selectedCodes.location_id && this.selectedCodes.location_id.length > 0) ? (this.allCodes.locations.find(({id}) => this.selectedCodes.location_id.includes(id)).code) : 'All',
              sets: (this.selectedCodes.set_id && this.selectedCodes.set_id.length > 0) ? (this.allCodes.sets.find(({id}) => this.selectedCodes.set_id.includes(id)).code) : 'All',
              free_fields: (this.selectedCodes.free_field_1_id && this.selectedCodes.free_field_1_id.length > 0) ? (this.allCodes.freefield1.find(({id}) => this.selectedCodes.free_field_1_id.includes(id)).code) : 'All',
              sourcecodes: (this.selectedCodes.source_code_id && this.selectedCodes.source_code_id.length > 0) ? (this.allCodes.sourcecodes.find(({id}) => this.selectedCodes.source_code_id.includes(id)).code) : 'All',
              accounts: capitalizeFirstLetter(this.reportOptions.accountQuickPick),
              ids: undefined,
              bank: selectedBank.code,
              account_number: selectedBank.account_number,
              statement_date: this.selectedBankRec.end,
              statement_balance: this.selectedBankRec.balance_decimal,
              previous_balance: previousBalance,
              cleared_checks_num: '',
              cleared_other_num: '',
              outstanding_checks_num: '',
              outstanding_other_num: '',
              cleared_checks: '',
              cleared_other: '',
              cleared_amount: 0,
              outstanding_checks: '',
              outstanding_other: '',
              outstanding_amount: 0,
              total_amount: 0,
              data: []
            }
          }

          let rawData = {}
          let checks = []
          let other = []
          let outstandingChecks = []
          let outstandingOther = []

          let cashCode = this.allCodes.accounts.find(({id}) => id === selectedBank.default_account_cash).code
          sortedAccounts.current[cashCode].map(line => {
            let data = {}
            data.payment_type = line.trans_type
            data.payment_date = line.transaction_payment_date || line.doc_date
            data.vendor_name = line.transaction_vendor_name
            data.description = line.line_desc
            data.trans_number = line.trans_number
            data.status = line.transaction_status
            data.recon = line.recon_id
            data.amount = line.amount

            // Current Payments
            if ((data.payment_type === 'CK' || data.payment_type === 'EFT' || data.payment_type === 'WIRE' || data.payment_type === 'MC') && data.recon === this.selectedBankRec.id) {
              data.trans_number = ''
              data.payment_number = line.doc_number
              data.amount = data.amount * -1
              checks.push(data)
            } else if (data.payment_type === 'AP' && data.recon === this.selectedBankRec.id) {
              data.amount = data.amount * -1
              data.payment_number = line.payment_number || line.transaction_payment_confirmation_number || ''
              checks.push(data)

            // Outstanding Payments to include future recon'd items
            } else if (data.payment_type === 'CK' && (data.recon === null || data.recon > this.selectedBankRec.id) && (this.reportOptions.excludeOutstanding ? getSimpleDate(data.payment_date) <= getSimpleDate(this.statement_date) : true)) {
              data.trans_number = ''
              data.payment_number = line.doc_number
              data.amount = data.amount * -1
              outstandingChecks.push(data)
            } else if (data.payment_type === 'AP' && (data.recon === null || data.recon > this.selectedBankRec.id) && (this.reportOptions.excludeOutstanding ? getSimpleDate(data.payment_date) <= getSimpleDate(this.statement_date) : true)) {
              data.amount = data.amount * -1
              data.payment_number = line.payment_number || line.transaction_payment_confirmation_number || ''
              outstandingChecks.push(data)

            // Current non-payments
            } else if (data.recon === this.selectedBankRec.id) {
              data.payment_number = line.payment_number || line.transaction_payment_confirmation_number || ''
              other.push(data)

            // Outstanding non-payments
            } else if (
                (data.recon === null || data.recon > this.selectedBankRec.id) && (this.reportOptions.excludeOutstanding ? getSimpleDate(data.payment_date) <= getSimpleDate(this.statement_date) : true)) {
              data.payment_number = line.payment_number || line.transaction_payment_confirmation_number || ''
              outstandingOther.push(data)

            // Everything here must have been previously reconciled.
            } else {
              // console.log(line)
            }
          })

          rawData.checks = sortObjectByProperty(checks, 'payment_number')
          rawData.other = other
          rawData.outstanding_checks = sortObjectByProperty(outstandingChecks, 'payment_number')
          rawData.outstanding_other = outstandingOther
          json.report.cleared_checks_num = checks.length
          json.report.cleared_other_num = other.length
          json.report.outstanding_checks_num = outstandingChecks.length
          json.report.outstanding_other_num = outstandingOther.length
          json.report.cleared_checks = this.sumRecursive(checks, 'amount')
          json.report.cleared_other = this.sumRecursive(other, 'amount')
          json.report.cleared_amount = parseFloat(json.report.cleared_other) - parseFloat(json.report.cleared_checks)
          json.report.outstanding_checks = this.sumRecursive(outstandingChecks, 'amount')
          json.report.outstanding_other = this.sumRecursive(outstandingOther, 'amount')
          json.report.outstanding_amount = parseFloat(json.report.outstanding_other) - parseFloat(json.report.outstanding_checks)
          json.report.total_amount = parseFloat(previousBalance) + parseFloat(json.report.cleared_amount) + parseFloat(json.report.outstanding_amount)
          json.report.ledger_amount = this.getAccountBalance(selectedBank.default_account_cash).balance
          json.report.data = rawData

          resolve(json)
        })
      },

      generateReport () {
        this.loading = true

        /*
        this.getTransactions(this.selectedCodes)
          .then(response => {
            return this.parseTransactions(this.reportOptions.sort)
          })
          .then((resTrans) => {
            return this.sortArrayOfTransactions(resTrans)
          })
        */
        this.getReportData('glreport')
          .then(resSorted => this.buildReconReport(resSorted.data))
          .then(json => {
            this.reportJson = json
            this.prettyJson = JSON.stringify(json, null, 2)
            return this.getJsonUrl(json)
          })
          .then(resJsonUrl => {
            // PDF version of the report
            let report = this.jasperSettings.reportServer + this.jasperSettings.reportName + '.pdf?net.sf.jasperreports.json.source=' + resJsonUrl
            // HTML version of the report
            let preview = this.jasperSettings.reportServer + this.jasperSettings.reportName + '.html?net.sf.jasperreports.json.source=' + resJsonUrl
            return this.getReportUrl(report)
          })
          .then(reportUrl => {
            this.loading = false
            this.createNotification('Success', 'Report is Ready. ', 'success', reportUrl)
            this.storeRenderedReport('Bank Reconciliation', 'PDF', reportUrl)
          })
          .catch(e => {
            this.loading = false
            console.log(e)
          })
      }
    },

    beforeCreate () {
    },

    created () {
      // Load data to be used for validation
      this.loading = true

      let additionalCodes = [ 'fetchBanks' ]
      this.getCompanyCodes(additionalCodes)
        .then(res => {
          this.allCodes.banks = this.getBanks
          this.allCodes.currencies = this.getCurrencies

          // Pre-load a bank statement
          if (this.$route.query) {
            const query = this.$route.query
            if (!(query || {}).bankrec_id) return false

            this.$store.dispatch('fetchBankRecsWithQuery', { query })
              .then(response => {
                if (response && response.entities && response.entities.bankRecs && response.entities.bankRecs[query.bankrec_id]) {
                  this.preLoadedStatement = response.entities.bankRecs[query.bankrec_id] || {}
                  this.selectedCodes.bank_id = this.preLoadedStatement.bank
                  this.selectedReconId = this.preLoadedStatement.id
                  // Run the same side-effects as a manual bank selection (@change never fires for a
                  // programmatic v-model change): load the bank's currency, cash-account balance and
                  // statement list, and re-append this draft statement. Without this the report would
                  // fail on the preselected bank (missing currency/account balance).
                  this.updateBank()
                }
              })
          }
        })
        .catch(error => {
          this.loading = false
          console.log(error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    mounted () {
    },

    beforeUnmount () {
      this.resetComponent()
    }
  }

</script>
<style scoped>
  .small { font-size: 8px; }
  .right { float: right; text-align: right; padding: 0px 5px; }
  .left { float: left; text-align: left; padding: 0px 5px; }
  .selectRow { padding: 0px 20px; }
  .section { padding: 40px 20px 0px;  }
  .headerRow {  padding: 15px 20px;  }
  .headerRow:first-child {  border-bottom: 1px solid lightgray;  }
</style>
<style>
  .el-select { display: block; }
  .el-transfer-panel { width:36%; max-width: 350px }
  .el-transfer-panel__body { min-height: 400px }
  .el-transfer-panel__list { min-height: 360px }
  /* border: 1px solid #d1dbe5; box-shadow: 0 2px 4px rgba(0,0,0,.12),0 0 6px rgba(0,0,0,.04); display: inline-block; width: 30%; position: relative; height: 500px; */
</style>
